import { useState } from "react";
import { openFile, fmtBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, FileCheck2, ShieldCheck, Eye, Trash2, Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export const DOC_KINDS = {
  lease: { label: "Signed lease", icon: FileCheck2, tone: "bg-emerald-50 text-emerald-700" },
  section8: { label: "Section 8 paperwork", icon: ShieldCheck, tone: "bg-amber-50 text-amber-700" },
  move_in: { label: "Move-in checklist", icon: ClipboardCheck, tone: "bg-indigo-50 text-indigo-700" },
  move_out: { label: "Move-out statement", icon: ClipboardCheck, tone: "bg-rose-50 text-rose-700" },
  other: { label: "Other document", icon: FileText, tone: "bg-slate-100 text-slate-600" },
};

export function DocumentList({ docs, onDelete, emptyText = "No documents yet." }) {
  const [busy, setBusy] = useState("");

  const view = async (d) => {
    setBusy(d.id);
    try { await openFile(`/documents/${d.id}/download`); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };

  if (!docs.length) return <p className="text-sm text-slate-500 text-center py-6" data-testid="docs-empty">{emptyText}</p>;

  return (
    <div className="space-y-2" data-testid="doc-list">
      {docs.map((d) => {
        const k = DOC_KINDS[d.kind] || DOC_KINDS.other;
        const Icon = k.icon;
        return (
          <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`doc-row-${d.id}`}>
            <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${k.tone}`}><Icon className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{d.title || d.filename}</div>
              <div className="text-xs text-slate-500 truncate">{k.label} · {d.filename} · {fmtBytes(d.size)} · {new Date(d.created_at).toLocaleDateString()}</div>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => view(d)} disabled={busy === d.id} data-testid={`doc-view-${d.id}`}>
              {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} View
            </Button>
            {onDelete && (
              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-rose-600 hover:bg-rose-50" onClick={() => onDelete(d)} data-testid={`doc-delete-${d.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
